import { useLayoutEffect, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { StatefulButton } from '@edx/paragon';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { DEFAULT_REDIRECT_URL } from '../../data/constants';
import { getActivationStatus } from '../../data/utils/dataUtils';
import FieldRenderer from '../../field-renderer/FieldRenderer';
import { frameSafeRedirect } from '../data/utils';
import useMarketingPreference from '../hooks/useMarketingPreference';
import messages from '../messages';
import TagManager from 'react-gtm-module';
import { getOrCreateLocalGuid } from '../../login/data/gtm';

const RegistrationConfirmation = ({
  formFields,
  queryParams,
  handleGoToNextStep,
  currentProvider,
}) => {
  const [failedVerification, setFailedVerification] = useState(false);

  const { authenticatedUser } = useSelector(state => state.commonComponents);

  const { formatMessage } = useIntl();

  const navigate = useNavigate();

  const isNextParam = queryParams.next && queryParams.next !== '/';

  const linkDestination = isNextParam ? queryParams.next : getConfig().CAMPUS_LOGO_URL;

  const linkMessage = isNextParam
    ? formatMessage(messages['registration.confirmation.page.course.text'])
    : formatMessage(messages['registration.confirmation.page.catalog.text']);

  const activationStatus = getActivationStatus();

  const { isMarketable, handleChange } = useMarketingPreference();

  useLayoutEffect(() => {
    if (activationStatus === 'info') {
      window.location.href = getConfig().LMS_BASE_URL.concat(DEFAULT_REDIRECT_URL);
    }

    if (activationStatus === 'error') {
      setFailedVerification(true);
    }
  }, [navigate, activationStatus]);

  if (activationStatus === 'info') {
    return null;
  }
 
  let hasFired = useRef(false)
  
  useEffect(() => {
    if (currentProvider && !hasFired.current) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'registration_success',
          local_guid: getOrCreateLocalGuid(),
          provider: currentProvider,
          page_path: location.pathname + location.search,
        },
      });
      hasFired.current = true;
    }
  }, [activationStatus, currentProvider, location]);

  return (
    <div
      className="flex-center-column flex-fill registration-confirmation"
      data-gtm={
        currentProvider
          ? `sso_registration_success_${currentProvider.toLowerCase()}`
          : 'manual_registration_success'
      }
    >
      <div className="h-70 d-flex justify-content-between flex-column">
        <div className="flex-center-column gap-20">
          <h1 className="page-title">
            {!failedVerification
              ? formatMessage(messages['registration.confirmation.page.title'], { fullName: formFields?.full_name || authenticatedUser?.username || '', enter: <br /> })
              : formatMessage(messages['registration.confirmation.page.could.not.verify.title'], { enter: <br /> })}
          </h1>
          <p className="responsive-text text-center my-3">
            {!failedVerification
              ? formatMessage(messages['registration.confirmation.page.subtitle'], { enter: <br /> })
              : formatMessage(messages['registration.confirmation.page.could.not.verify.subtitle'], { enter: <br /> })}
          </p>
          {!failedVerification && (
            <FieldRenderer
              className="cursor-pointer"
              value={isMarketable}
              fieldData={{ type: 'checkbox', label: formatMessage(messages['accept.recieving.courses.and.projects.updates']) }}
              onChangeHandler={handleChange}
            />
          )}
        </div>
        <div className="flex-center-column">
          {!failedVerification && (
            <StatefulButton
              id="next-step"
              name="next-step"
              type="button"
              className="logistration-btn my-5"
              labels={{
                default: formatMessage(messages['registration.confirmation.page.button.text']),
                pending: '',
              }}
              onClick={handleGoToNextStep}
            />
          )}
          <button
            type="button"
            onClick={() => frameSafeRedirect(linkDestination)}
            className="link-style responsive-text my-4 dark-green-color"
          >
            {!failedVerification
              ? linkMessage
              : formatMessage(messages['registration.confirmation.page.could.not.verify.contact.support.text'])}
          </button>
        </div>
      </div>
    </div>
  );
};

RegistrationConfirmation.propTypes = {
  queryParams: PropTypes.shape({
    next: PropTypes.string,
    finalRedirect: PropTypes.string,
  }).isRequired,
  handleGoToNextStep: PropTypes.func.isRequired,
};

export default RegistrationConfirmation;
